'use client';

import type { INavListItem, INavListItemContentListItem } from '@/api/types/public';
import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { setSession } from '@/utils/tools';
import { setCampaignEntrancePageScene } from '@/utils/tracking/entrancePageScene';
import styles from './index.module.scss';

interface ImageNavigationNavProps {
  item: INavListItem;
  firstLevelIndex: number;
  firstLevelTitle: string;
  isDarkMode: boolean;
}

const getShopSku = (sku?: INavListItemContentListItem['sku']) =>
  Array.isArray(sku) ? sku[0] : sku?.shop_sku;

const ImageNavigationNav = ({
  item,
  firstLevelIndex,
  firstLevelTitle,
  isDarkMode,
}: ImageNavigationNavProps) => {
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();
  const content = item.content;
  const imageItems = content?.list?.filter((listItem) => Boolean(listItem.jump_image)) || [];

  if (imageItems.length === 0) return null;

  const handleBeforeJump = (listItem: INavListItemContentListItem, imageIndex: number) => {
    setSession('zl_goodsListEnterPageScene', '全局导航');
    setCampaignEntrancePageScene('顶部导航', listItem.Jump_target || undefined);
    trackEvent(
      {
        eventName: 'TopNavigationMkt_Click',
        properties: {
          page_name: cnName,
          url_path: window.location.href,
          tab_name: firstLevelTitle,
          tab_index: firstLevelIndex,
          pits_index: imageIndex,
          pits_title: listItem.title || '',
        },
      },
      [ProviderType.SENSORS],
    );
  };

  return (
    <section
      className={`${styles.imageNavigationModule} ${isDarkMode ? 'text-white' : 'text-[#282828]'}`}
      aria-label={content?.layout_title || undefined}
    >
      {content?.layout_title ? (
        <h2 className="font-bb text-[16px] leading-[19px]">{content.layout_title}</h2>
      ) : null}

      <div className="flex gap-[8px]">
        {imageItems.map((listItem, imageIndex) => {
          const title = listItem.title || '';
          const jumpTarget = {
            jump_type: listItem.jump_type ?? 0,
            Jump_target: listItem.Jump_target || '',
            product_group: {
              id: listItem.product_group?.id ?? undefined,
              path: listItem.product_group?.path,
            },
            sku: {
              shop_sku: getShopSku(listItem.sku) || '',
            },
            crowd_recommend: {
              id: listItem.crowd_recommend?.id ?? '',
            },
          };

          return (
            <MagicJump
              key={listItem.id ?? `${title}-${imageIndex}`}
              className={`${styles.imageNavigationCard} group block shrink-0 overflow-hidden`}
              item={jumpTarget}
              onBeforeJump={() => handleBeforeJump(listItem, imageIndex)}
            >
              <div className="relative aspect-[196/245] w-full overflow-hidden">
                <Image
                  src={listItem.jump_image || ''}
                  alt={title}
                  fill
                  sizes="(max-width: 1366px) 11vw, 196px"
                  className="object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
              </div>
              {title ? (
                <div className="truncate py-[8px] text-center text-[14px] leading-[17px]">
                  {title}
                </div>
              ) : null}
            </MagicJump>
          );
        })}
      </div>
    </section>
  );
};

export default ImageNavigationNav;
